import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDrawer, MatPaginator, MatSort } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, zip } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { DataManagementService, DateService, DeviceService, HttpService, ScreenSize, TranslateService } from 'vas-mobile-api';

import { Chip, Column, Field, FieldType, MobileDataView, Operator, Option } from '../models/chips-and-fields';
import { DeliveriesDataSource, Filter } from '../models/deliveriesDataSource';
import { Delivery } from '../models/delivery';
import { DataExchangeService } from '../services/dataexchange.service';

@Component({
  selector: 'app-status-list',
  templateUrl: './status-list.component.html',
  styleUrls: ['./status-list.component.css']
})
export class StatusListComponent implements OnInit, AfterViewInit, OnDestroy {

  // view configs
  public columns: Column[];
  public mobileDataView: MobileDataView;
  public columnKeys: string[];
  public filterFields: Field[];

  public dataSource: DeliveriesDataSource;
  private paginator: MatPaginator;
  private sort: MatSort;
  @ViewChild(MatDrawer, { static: false }) private drawerRight: MatDrawer;
  private previousFilters: any;

  // Keep this till end of test. This is filter ov ABX.
  // private defaultFilters: Filter[] = [
  //   {
  //     key: 'LIEFERSCHEINSTATUS',
  //     value: 'L',
  //     operator: Operator.NOT
  //   }, {
  //     key: 'VORGANGS_CODE',
  //     value: 'ABX',
  //     operator: Operator.NOT
  //   }, {
  //     key: 'VORGANGS_CODE',
  //     value: 'AGX',
  //     operator: Operator.NOT
  //   }];

  // Removed all Filters, this will now handled in the backend
  private defaultFilters: Filter[] = [
    {
      key: 'LIEFERSCHEINSTATUS',
      value: 'L',
      operator: Operator.NOT
    }];

  // a little hack for the HTML
  public fieldType = FieldType;

  public chips: Chip[] = [];
  public filterForm: FormGroup;
  public mobileView: boolean;
  private ngUnsubscribe = new Subject();
  private mobileDeliveries: Delivery[] = [];
  private mobileLimit = 20;
  public mobileDeliveriesSelection: Delivery[];
  private loading: boolean;

  constructor(
    private httpService: HttpService,
    private router: Router,
    private des: DataExchangeService,
    private formBuilder: FormBuilder,
    private dateService: DateService,
    private deviceService: DeviceService,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private dataMsgSrv: DataManagementService
  ) { }

  ngOnInit() {
    this.filterForm = new FormGroup({});
    this.dataMsgSrv.getUserType().then((userType) => {
      let pathConfig = '';
      if (userType === 'SUPP') {
        pathConfig = 'assets/config/statusListConfig_inbound.json';
      } else {
        pathConfig = 'assets/config/statusListConfig.json';
      }
      const config$ = this.httpService.getLocalResource(pathConfig);
      const routeParams$ = this.route.queryParamMap.pipe(takeUntil(this.ngUnsubscribe));

      zip(config$, routeParams$).subscribe((results) => {
        const config: { columns: Column[], filters: Field[], mobileView: MobileDataView } = results[0];
        const paramMap = results[1];

        this.columns = config.columns;
        this.filterFields = config.filters;
        this.mobileDataView = config.mobileView;

        this.columnKeys = this.columns.map((v) => v.key);

        this.filterFields.forEach((field) => {
          let defaultOption: Option;
          if (field.options && (defaultOption = field.options.find((o) => {
            return o.selected;
          }))) {
            this.filterForm.addControl(field.formName, new FormControl(defaultOption.key));
          } else {
            this.filterForm.addControl(field.formName, new FormControl());
          }
        });

        paramMap.keys.forEach((key) => {
          const value = paramMap.get(key);
          this.filterForm.get(key).patchValue(value);
        });
        this.setChipsByFilters();
        const filters = this.convertChipsToFilters(this.chips);
        this.dataSource = new DeliveriesDataSource(this.httpService, this.columnKeys, filters);

        this.deviceService.size.pipe(takeUntil(this.ngUnsubscribe)).subscribe((size) => {
          this.mobileView = size <= ScreenSize.sm;
          this.dataSource.isMobile = this.mobileView;
        });

        this.dataSource.connect(null).pipe(takeUntil(this.ngUnsubscribe), filter(() => this.mobileView)).subscribe((data) => {
          this.mobileDeliveries = data;
          if (this.mobileDeliveries) {
            this.mobileDeliveriesSelection = this.mobileDeliveries.slice(0, this.mobileLimit);
          }
        });

        this.dataSource.loading$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((loading) => {
          this.loading = loading;
        });

        this.previousFilters = this.filterForm.value;

      });
    });


    this.des.filterToggle$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      if (this.drawerRight.opened) {
        this.drawerRight.close();
      } else {
        this.drawerRight.open();
      }
    });

    this.des.refresh$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      if (this.dataSource) {
        this.dataSource.refresh();
      }
    });

    this.des.changeTitelBarName('LBLPANEL_STATUSLIST');
  }

  ngAfterViewInit(): void {

    this.drawerRight.closedStart.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.filterForm.patchValue(this.previousFilters);
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.dataSource.disconnect(null);
  }

  @ViewChild(MatPaginator, { static: false }) set matPaginator(matPaginator: MatPaginator) {
    this.paginator = matPaginator;
    this.setTableObjects();
  }

  @ViewChild(MatSort, { static: false }) set matSort(matSort: MatSort) {
    this.sort = matSort;
    this.setTableObjects();
  }

  private setTableObjects() {
    if (this.dataSource && this.sort && this.paginator) {
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
    }
  }

  public navigateDelivery(id: string) {
    this.router.navigate(['deliveryDetail', id]);
  }

  public removeChipAndRefresh(key: string) {
    this.removeChip(key);
    const filters = this.convertChipsToFilters(this.chips);
    this.dataSource.setFilters(filters);
  }

  private removeChip(key: string) {
    const idx = this.chips.findIndex((chip) => chip.key === key);
    if (idx >= 0) {
      this.chips.splice(idx, 1);
      this.filterForm.get(key).reset();
      this.previousFilters = this.filterForm.value;
    }
  }

  public setFilters() {
    if (this.filterForm.valid) {
      // bug introduced in angular material 6.0.0
      (<HTMLElement>document.activeElement).blur();
      this.previousFilters = this.filterForm.value;
      this.setChipsByFilters();
      const filters = this.convertChipsToFilters(this.chips);
      this.dataSource.setFilters(filters);
      this.drawerRight.close();
    }
  }

  private setChipsByFilters() {
    this.filterFields.forEach((field) => {
      const value = this.filterForm.get(field.formName).value;
      if (value) {

        let displayValue;

        // handle select and radio values
        if (field.type === FieldType.RADIO || field.type === FieldType.SELECT) {
          displayValue = field.options.find((o) => o.key === value).value;
          displayValue = this.translateService.translateFirstLetterUpperCase(displayValue);
        }

        // handle date values
        if (field.type === FieldType.DATE) {
          displayValue = this.dateService.getReadableDate(value);
        }

        const idx = this.chips.findIndex((chip) => chip.key === field.formName);
        if (idx >= 0) {
          this.chips[idx].value = value;
          this.chips[idx].displayValue = displayValue;
        } else {
          this.chips.push({
            key: field.formName,
            value: value,
            displayName: field.displayName,
            displayValue: displayValue
          });
        }
      } else {
        this.removeChip(field.formName);
      }
    });
  }

  public resetFilters() {
    // this.chips = [];
    // this.previousFilters = null;
    // this.drawerRight.close();
    // this.dataSource.setFilters(this.defaultFilters);
  }

  private convertChipsToFilters(chips: Chip[]): Filter[] {
    const filters: Filter[] = [];
    chips.forEach((chip) => {
      const field = this.filterFields.find((f) => {
        return f.formName === chip.key;
      });


      let value = chip.value;
      let operator = field.operator;

      // handle wildcard
      if (operator === Operator.EQUAL && chip.value.indexOf('*') > -1) {
        operator = Operator.LIKE;
        value = value.replace('*', '%');
      }

      // handle checkbox
      if (field.type === FieldType.CHECKBOX) {
        value = value ? 'Y' : 'N';
      }

      // handle date
      if (field.type === FieldType.DATE) {
        value = this.dateService.getDate(new Date(value));
      }

      filters.push({
        key: field.columnName || field.formName, // a column name is optional but is prioritised
        value: value,
        operator: operator
      });
    });
    filters.push(...this.defaultFilters);
    return filters;
  }

  public onScrollDown() {
    if (this.mobileView && !this.loading) {
      this.mobileLimit += 20;
      this.mobileDeliveriesSelection = this.mobileDeliveries.slice(0, this.mobileLimit);
    }
  }

  public getOptionValue(options: Option[], key: string) {
    if (key) {
      const option = options.find((o) => o.key === key);
      return option ? option.value : key;
    }
    return '';
  }

}
